import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Sélecteur de mois dont la valeur est « AAAA-MM », la forme que le contrat
 * attend (Yros/Kavero#426).
 *
 * `type="month"` n'existe ni sur Safari de bureau ni sur Firefox de bureau :
 * le champ y devient une zone de texte sans sélecteur. Là où le navigateur le
 * gère, on garde le champ natif ; ailleurs, deux listes — mois, année — qui
 * composent la même valeur. Dans les deux cas le parent reçoit une chaîne
 * `AAAA-MM`, ou `''` tant que la saisie est incomplète.
 */

// Années proposées à partir de celle du minimum. Un prêt est borné par la
// société (`yros_rh_duree_max_pret`), pas par l'écran : dix ans couvrent tout
// échéancier réaliste sans liste interminable.
const ANNEES_PROPOSEES = 10;

const CLASSE_LISTE =
  'mt-1 w-full px-4 py-2 border rounded-lg text-gray-700 bg-gray-50 font-medium text-sm';

export const supporteMoisNatif = () => {
  if (typeof document === 'undefined') return true;
  const champ = document.createElement('input');
  champ.setAttribute('type', 'month');
  return champ.type === 'month';
};

const decouper = (valeur) => {
  const [annee = '', mois = ''] = (valeur || '').split('-');
  return { annee, mois };
};

function SelecteurMois({
  value,
  onChange,
  min,
  testId,
  className = CLASSE_LISTE,
  natif = supporteMoisNatif(),
}) {
  const { t, i18n } = useTranslation();
  const [partiel, setPartiel] = useState({ annee: '', mois: '' });

  const libelles = useMemo(() => {
    const format = new Intl.DateTimeFormat(i18n.language || 'fr', { month: 'long' });
    return Array.from({ length: 12 }, (_, i) => format.format(new Date(2000, i, 1)));
  }, [i18n.language]);

  if (natif) {
    return (
      <input
        type="month"
        data-testid={testId}
        min={min}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={className}
      />
    );
  }

  const choix = value ? decouper(value) : partiel;
  const borne = min ? decouper(min) : null;
  const anneeMin = borne ? Number(borne.annee) : new Date().getFullYear();
  const annees = Array.from({ length: ANNEES_PROPOSEES }, (_, i) => String(anneeMin + i));
  const sousLeMinimum = (annee, mois) =>
    Boolean(borne) && Boolean(annee) && annee === borne.annee && mois < borne.mois;

  const appliquer = (annee, mois) => {
    // Un mois devenu interdit par le changement d'année est retiré, plutôt que
    // de laisser partir un mois passé que l'écran n'a jamais proposé.
    const moisRetenu = sousLeMinimum(annee, mois) ? '' : mois;
    if (annee && moisRetenu) {
      setPartiel({ annee: '', mois: '' });
      onChange(`${annee}-${moisRetenu}`);
      return;
    }
    setPartiel({ annee, mois: moisRetenu });
    if (value) onChange('');
  };

  return (
    <div data-testid={testId} data-min={min || undefined} className="grid grid-cols-2 gap-2">
      <select
        data-testid={`${testId}-mois`}
        aria-label={t('selecteurMois.mois')}
        value={choix.mois}
        onChange={(e) => appliquer(choix.annee, e.target.value)}
        className={className}
      >
        <option value="">{t('selecteurMois.mois')}</option>
        {libelles.map((libelle, i) => {
          const numero = String(i + 1).padStart(2, '0');
          if (sousLeMinimum(choix.annee, numero)) return null;
          return (
            <option key={numero} value={numero}>
              {libelle}
            </option>
          );
        })}
      </select>
      <select
        data-testid={`${testId}-annee`}
        aria-label={t('selecteurMois.annee')}
        value={choix.annee}
        onChange={(e) => appliquer(e.target.value, choix.mois)}
        className={className}
      >
        <option value="">{t('selecteurMois.annee')}</option>
        {annees.map((annee) => (
          <option key={annee} value={annee}>
            {annee}
          </option>
        ))}
      </select>
    </div>
  );
}

export default SelecteurMois;
